import { CartOrder } from 'cart-core';
import { ChangeHandler } from 'forms';
import React from 'react';
import useFieldHasError from '../../hooks/useFieldHasError';
import { LegacyCartFormMessage } from '../../types';
import CreditCardExpirationDateFields from './CreditCardExpirationDateFields';
import CreditCardNameField from './CreditCardNameField';
import CreditCardNumberField from './CreditCardNumberField';
import CreditCardVerificationCodeField from './CreditCardVerificationCodeField';

interface Props {
  /**
   * Current value of the card number field.
   */
  cardNumber: string;

  /**
   * Whether or not fields should be disabled.
   *
   * Default is `false`.
   */
  disabled?: boolean;

  /**
   * Current value of the expiration month sub-field, which is part of the expiration date field.
   */
  expirationMonth: number;

  /**
   * Current value of the expiration year sub-field, which is part of the expiration month field.
   */
  expirationYear: number;

  /**
   * Collection of messages describing current meta-state of fields.
   *
   * This is used to determine whether fields should be shown in an error state.
   */
  messages?: LegacyCartFormMessage[];

  /**
   * Current value of the name-on-card field.
   */
  nameOnCard: string;

  /**
   * Called whenever the user changes the card number field.
   */
  onCardNumberChange: ChangeHandler<string>;

  /**
   * Called whenever the user changes the the expiration date's month sub-field.
   */
  onExpirationMonthChange: ChangeHandler<number>;

  /**
   * Called whenever the user changes the expiration date's year sub-field.
   */
  onExpirationYearChange: ChangeHandler<number>;

  /**
   * Called whenever the user changes the name-on-card field.
   */
  onNameOnCardChange: ChangeHandler<string>;

  /**
   * Called whenever the user changes the verification code field.
   */
  onVerificationCodeChange: ChangeHandler<string>;

  /**
   * Used to determine auxiliary visual state based on an order.
   *
   * @todo In the future, this could be determined implicitly using React context.
   */
  order: CartOrder;

  /**
   * Current value of the verification code field.
   */
  verificationCode: string;
}

/**
 * Renders a collection of fields used to acquire standard credit card form data.
 *
 * @todo
 * 2022-05-23 - "Brad Harms" <brad.k.harms@gmail.com>
 *
 * This was designed to work in conjunction with the `forms` library, but
 * the forms library actually allows a single value change handler to provide
 * values for multiple fields distinguished by name. It might be cleaner to
 * make use of that feature to receive value changes rather than to receive each
 * value via its own unique handler callback property. (Or not?)
 */
const CreditCardFields = ({
  cardNumber,
  disabled,
  expirationMonth,
  expirationYear,
  messages,
  nameOnCard,
  onCardNumberChange,
  onExpirationMonthChange,
  onExpirationYearChange,
  onNameOnCardChange,
  onVerificationCodeChange,
  order,
  verificationCode,
}: Props) => (
  <>
    <CreditCardNumberField
      disabled={disabled}
      error={useFieldHasError(messages, 'cardNumber')}
      onValueChange={onCardNumberChange}
      value={cardNumber}
    />
    <CreditCardNameField
      disabled={disabled}
      error={useFieldHasError(messages, 'nameOnCard')}
      onValueChange={onNameOnCardChange}
      value={nameOnCard}
    />
    <CreditCardExpirationDateFields
      disabled={disabled}
      error={useFieldHasError(messages, 'expirationMonth', 'expirationYear')}
      expirationMonth={expirationMonth}
      expirationYear={expirationYear}
      onExpirationMonthChange={onExpirationMonthChange}
      onExpirationYearChange={onExpirationYearChange}
    />
    <CreditCardVerificationCodeField
      order={order}
      disabled={disabled}
      error={useFieldHasError(messages, 'verificationCode')}
      onValueChange={onVerificationCodeChange}
      value={verificationCode}
    />
  </>
);

export default CreditCardFields;
